import i18n from '@/core/translations/i18n';
import { SET_APP_LOADING } from '@/core/store/action-types';
import {
  AddRoomObjectDTO,
  LightType,
  PlantDetailDTO,
  RoomDTO,
  RoomObjectType,
  ThermoAssociatedPump,
  ThermoTypeOfObject,
} from '@/../client-generator/generated-clients/plants';
import { Vue, Options } from 'vue-class-component';
import NewRoomObject from './NewRoomObject/NewRoomObject.vue';
import plantsClient from '@/core/services/plants';
import { PropType } from 'vue';
import { HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import {
  IValidableObjectType,
  NamingService,
  RoomObjectsTypes,
} from '@/core/services/naming.service';
import {
  PlantEntityType,
  PlantFlatEntityDTO,
} from '@/../client-generator/generated-clients/configurations';
import {
  getObjectIconFromType,
  getObjectLabelFromType,
} from '@/core/utils/utils';

const $t = i18n.global.t;
@Options({
  name: 'AddRoomObjectDialog',
  components: {
    NewRoomObject,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    room: {
      required: true,
    },
    currentPlant: {
      type: Object as PropType<PlantDetailDTO>,
      required: false,
    },
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (!this.isDialogVisible) {
        this.objectTypes.forEach((ot: RoomObjectsTypes) => (ot.objects = []));
        NamingService.flushCache();
      }
    },
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class AddRoomObjectDialog extends Vue {
  room!: RoomDTO;
  currentPlant!: PlantDetailDTO;
  isVisible!: boolean;
  isDialogVisible = false;

  getObjectIconFromType = getObjectIconFromType;
  getObjectLabelFromType = getObjectLabelFromType;

  expandedItems = [];
  objectTypes: RoomObjectsTypes[] = [
    {
      id: 'blind',
      objects: [],
      type: RoomObjectType.Blind,
    },
    {
      id: 'light',
      objects: [],
      type: RoomObjectType.Light,
    },
    {
      id: 'colored-light',
      objects: [],
      type: RoomObjectType.ColoredLight,
    },
    {
      id: 'Lock',
      objects: [],
      type: RoomObjectType.Lock,
    },
    {
      id: 'shutter',
      objects: [],
      type: RoomObjectType.Shutter,
    },
    {
      id: 'socket',
      objects: [],
      type: RoomObjectType.Socket,
    },
    {
      id: 'thermostat',
      objects: [],
      type: RoomObjectType.Thermostat,
    },
    {
      id: 'dimmerDmx',
      objects: [],
      type: RoomObjectType.DimmerDmx,
    },
    {
      id: 'fancoil',
      objects: [],
      type: RoomObjectType.Fancoil,
    },
    {
      id: 'xmeter',
      objects: [],
      type: RoomObjectType.Xmeter,
    },
    {
      id: 'player',
      objects: [],
      type: RoomObjectType.Player,
    },
    {
      id: 'towelWarmer',
      objects: [],
      type: RoomObjectType.TowelWarmer,
    },
  ];

  get anyNameIsUnderComputation(): boolean {
    return !!this.objectTypes.find((ot) => ot.busy);
  }

  // Room object types that occupy an automation address (kept in sync with the backend calculation)
  private static readonly ADDRESS_CONSUMING_TYPES = [
    RoomObjectType.Blind,
    RoomObjectType.Lock,
    RoomObjectType.Socket,
    RoomObjectType.Shutter,
    RoomObjectType.Light,
  ];

  isAddObjectDisabled(objectType: RoomObjectsTypes): boolean {
    return (
      !this.currentPlant.availableAutomationAddresses &&
      AddRoomObjectDialog.ADDRESS_CONSUMING_TYPES.includes(objectType.type)
    );
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;

    await this.addObjects();

    await this.$nextTick(async () => {
      this.$emit('update-list');
      this.resetObjects();
      this.$toast.add({
        severity: 'success',
        summary: this.$t('plantDetail.objectsCreated'),
        closable: false,
        life: 3000,
      });
      const summary = this.$t('general.synced');
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: summary,
          closable: false,
          life: 3000,
        });
      }, 1500);
    });
  }

  async addObjects() {
    if (this.currentPlant.desktopId === undefined) return;
    const roomId = this.room.id ?? (this.$route.params.roomId as string);

    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const request: Array<AddRoomObjectDTO> = this.objectTypes
        .map((k) => k.objects)
        .flat();

      await plantsClient.roomObjectsPOST(
        this.currentPlant.desktopId,
        roomId,
        request
      );
    } catch (error) {
      let messageError = '';

      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }

      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  handleRoomObjectChange(e: IValidableObjectType) {
    this.validateChanges(e);
  }

  private async validateChanges(e: IValidableObjectType) {
    // we want to prevent vue's change detections on this list while
    // we are editing it
    let completeObjectsListClone = [...this.objectTypes];
    const parentObjType = completeObjectsListClone.find(
      (objType) => objType.type === e.type
    );

    // re-assign the new object on the cloned parent
    parentObjType!.objects[e.idx!] = e;
    const parentObjTypeIdx = completeObjectsListClone.findIndex(
      (o) => o.type === e.type
    );
    if (parentObjTypeIdx === -1)
      throw new Error("Can't find parent obj type in cloned list of objects");

    // re-assign the object type parent on the cloned list
    completeObjectsListClone[parentObjTypeIdx] = parentObjType!;

    // validate the cloned list
    completeObjectsListClone = await NamingService.validateRoomObjectAgainstList(
      completeObjectsListClone,
      e
    );

    this.updateAdditionalEntitiesList();

    for (const parentObject of completeObjectsListClone) {
      for (const innerObject of parentObject.objects) {
        if (innerObject.isInvalid) {
          let suggestedName: string = await NamingService.suggestNextValidEntityName(
            {
              eType: PlantEntityType.ROOM_OBJECT,
              plantId: this.currentPlant.desktopId as any,
              metadata: {
                room: this.room,
                roomType: this.room.type,
                roomObjectType: innerObject.type,
              },
            }
          );
          innerObject.invalidReason = this.$t(
            `uniqueNaming.validationErrorWithSuggestion`,
            { suggestedName }
          );
        }
      }
    }

    this.objectTypes = completeObjectsListClone;
  }

  private objectsTypesToFlatPlantEntitiesList(): PlantFlatEntityDTO[] {
    const data: PlantFlatEntityDTO[][] = this.objectTypes.map(
      (objType: RoomObjectsTypes) => {
        return objType.objects.map((obj) => {
          return new PlantFlatEntityDTO({
            currentName: obj.name,
            id: null!,
            objectType: obj.type,
            plantEntityType: PlantEntityType.ROOM_OBJECT,
          });
        });
      }
    );
    const newList = [...data.flatMap((d) => d)];
    return newList;
  }

  private updateAdditionalEntitiesList() {
    NamingService.additionalEntities = this.objectsTypesToFlatPlantEntitiesList();
  }

  async handleAddObject(objType: RoomObjectsTypes) {
    const objectType = { ...objType };
    objectType.busy = true;
    const suggestedName = await NamingService.suggestNextValidEntityName({
      eType: PlantEntityType.ROOM_OBJECT,
      plantId: this.currentPlant.desktopId as any,
      metadata: {
        room: this.room,
        roomType: this.room.type,
        roomObjectType: objectType.type,
      },
    });

    const objToAdd = new AddRoomObjectDTO({
      name: suggestedName,
      type: objectType.type,
      lightType:
        objectType.type === RoomObjectType.Light ? LightType.OnOff : undefined,
      isManagePosition:
        objectType.type === RoomObjectType.Shutter ? false : undefined,
      thermoTypeOfObject:
        objectType.type === RoomObjectType.Thermostat ||
        objectType.type === RoomObjectType.Fancoil
          ? ThermoTypeOfObject.Heating
          : undefined,
      thermoAssociatedPump:
        objectType.type === RoomObjectType.Thermostat
          ? ThermoAssociatedPump.Yes
          : undefined,
    });

    (objToAdd as IValidableObjectType).idx = objectType.objects.length;
    objectType.objects.push(objToAdd);
    objectType.busy = false;
    this.updateAdditionalEntitiesList();
  }

  handleObjectRemove(objectType: RoomObjectsTypes, index: number) {
    objectType.objects.splice(index, 1);
    this.updateAdditionalEntitiesList();
  }

  resetObjects() {
    this.objectTypes.forEach((p) => (p.objects = []));
  }
}
